import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

/**
 * Unified chart wrapper. One component, three chart types via the `type` prop.
 * All charts share our design-system rules so pages don't have to repeat them:
 *   - no axis lines, no tick lines
 *   - horizontal gridlines only, in border-light gray
 *   - custom tooltip with white surface + soft shadow
 *   - accent + graduated grays palette
 *
 * Props:
 *   type       : "line" | "bar" | "area"    (default "line")
 *   data       : array of objects
 *   xKey       : string — key on each datum for the X axis
 *   series     : [{ key, color, label? }]
 *   height     : number (default 240)
 *   stacked    : boolean (area + bar only; stacks series on top of each other
 *                so small series stay visible next to large ones)
 *   yFormatter : (value) => string          (optional)
 *   xFormatter : (value) => string          (optional)
 */
const AXIS_STYLE = { fontSize: 10, fill: "#a3a3a3" };
const GRID_COLOR = "#f0f0f0";

function CustomTooltip({ active, payload, label, labelFormatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="bg-surface border border-border-default rounded px-3 py-2 text-[11px]"
      style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
    >
      <div className="font-semibold text-text-primary mb-1.5">
        {labelFormatter ? labelFormatter(label) : label}
      </div>
      <div className="space-y-1">
        {payload.map((p) => (
          <div
            key={p.dataKey}
            className="flex items-center gap-2 text-text-secondary"
          >
            <span
              className="inline-block w-2 h-2 rounded-sm flex-shrink-0"
              style={{ backgroundColor: p.color }}
            />
            <span className="flex-1">{p.name || p.dataKey}</span>
            <span className="font-medium text-text-primary">
              {typeof p.value === "number" ? p.value.toLocaleString() : p.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Chart({
  type = "line",
  data,
  xKey,
  series,
  height = 240,
  stacked = false,
  yFormatter,
  xFormatter,
}) {
  const ChartImpl =
    type === "bar" ? BarChart : type === "area" ? AreaChart : LineChart;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ChartImpl data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={GRID_COLOR} vertical={false} />
        <XAxis
          dataKey={xKey}
          tick={AXIS_STYLE}
          axisLine={false}
          tickLine={false}
          tickFormatter={xFormatter}
          interval="preserveStartEnd"
          minTickGap={16}
        />
        <YAxis
          tick={AXIS_STYLE}
          axisLine={false}
          tickLine={false}
          width={40}
          tickFormatter={yFormatter}
        />
        <Tooltip
          content={<CustomTooltip labelFormatter={xFormatter} />}
          cursor={{ fill: GRID_COLOR, stroke: "none" }}
        />
        {series.map((s) =>
          renderSeries(type, s, stacked)
        )}
      </ChartImpl>
    </ResponsiveContainer>
  );
}

function renderSeries(type, s, stacked) {
  const common = {
    key: s.key,
    dataKey: s.key,
    name: s.label || s.key,
  };
  // Recharts stacks siblings that share a stackId — so passing the same id
  // to every series in the chart turns overlapping areas/bars into a stack.
  const stackProps = stacked ? { stackId: "stack" } : {};
  if (type === "bar") {
    return (
      <Bar
        {...common}
        {...stackProps}
        fill={s.color}
        radius={[3, 3, 0, 0]}
        maxBarSize={28}
      />
    );
  }
  if (type === "area") {
    return (
      <Area
        {...common}
        {...stackProps}
        type="monotone"
        stroke={s.color}
        fill={withAlpha(s.color, stacked ? "80" : "33")}
        strokeWidth={2}
        dot={false}
      />
    );
  }
  return (
    <Line
      {...common}
      type="monotone"
      stroke={s.color}
      strokeWidth={2}
      dot={false}
      activeDot={{ r: 4, stroke: "#fff", strokeWidth: 2 }}
    />
  );
}

function withAlpha(hex, aa) {
  return hex?.length === 7 ? hex + aa : hex;
}
